import { type FC, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Sticky from 'react-sticky-el';
import useMeasure from 'react-use-measure';
import { StorageHead } from '@components/Storage/StorageHead/StorageHead';
import { StorageNavigation } from '@components/Storage/StorageNavigation/StorageNavigation';
import TabsBody from '@components/Storage/TabsBody/TabsBody';
import cn from 'classnames';
import { t } from 'i18next';
import { type Swiper as SwiperInstance } from 'swiper';
import { Controller } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { TextXSmall } from '@/components/Typography/Typography';
import { Picture } from '@/components/ui/Picture/Picture';

import css from './styles.module.scss';

import warningPng from '/img/warning-small.png';
import warningWebp from '/img/warning-small.webp';

const Storage: FC = () => {
  const headRef = useRef<HTMLDivElement | null>(null);
  const [resourceRef, resourceDiv] = useMeasure();
  const [materialRef, materialDiv] = useMeasure();
  const [goodsRef, goodsDiv] = useMeasure();
  const listRef = useRef<HTMLDivElement | null>(null);
  const mainRef = useRef<HTMLDivElement | null>(null);
  // scroll contorller
  const [activeSlide, setActiveSlide] = useState(0);
  const [firstSwiper, setFirstSwiper] = useState<SwiperInstance | null>(null);
  const [secondSwiper, setSecondSwiper] = useState<SwiperInstance | null>(null);

  const changeSlide = (slide: number): void => {
    firstSwiper?.slideTo(slide);
    secondSwiper?.slideTo(slide);
  };

  const onSlideChange = (swiper: SwiperInstance): void => {
    setActiveSlide(swiper.activeIndex);
  };

  return (
    <main
      id="storage"
      ref={mainRef}
      style={{ overflowY: 'auto' }}
      className={cn('w-full relative h-full flex flex-col', css.swiper)}
    >
      <Helmet>
        <title>Storage</title>
      </Helmet>
      <div className={cn('w-full h-full')}>
        <Sticky
          topOffset={150}
          stickyStyle={{ top: '-150px' }}
          stickyClassName="z-20"
          scrollElement="#storage"
        >
          <div
            id="storage-head"
            ref={headRef}
            className={cn('w-full', css['gradient-bg'])}
          >
            <Swiper
              className="w-full"
              modules={[Controller]}
              onSwiper={setFirstSwiper}
              controller={{ control: secondSwiper }}
              onSlideChange={onSlideChange}
            >
              <SwiperSlide>
                <StorageHead kind="resource" />
              </SwiperSlide>
              <SwiperSlide>
                <StorageHead kind="material" />
              </SwiperSlide>
              <SwiperSlide>
                <StorageHead kind="goods" />
              </SwiperSlide>
            </Swiper>
            <div className="w-[calc(100%_-_1rem)] flex gap-1 items-center p-2 rounded-lg backdrop-blur bg-surface mt-2 mb-1 mx-2">
              <Picture
                className="w-8 h-8 min-w-8 min-h-8 shrink-0"
                img={warningPng}
                webp={warningWebp}
                alt="warning"
              />
              <TextXSmall className="!text-white !leading-1.2">
                {t(
                  'If something on your stock hits the red zone — don’t wait!',
                )}
                <br />
                {t('Over time, the excess might disappear.')}
              </TextXSmall>
            </div>
            <StorageNavigation
              changeSlide={changeSlide}
              activeIndex={activeSlide}
            />
          </div>
        </Sticky>
        <div ref={listRef}>
          <Swiper
            className={cn('w-full')}
            style={{
              height:
                activeSlide === 0
                  ? resourceDiv.height
                  : activeSlide === 1
                    ? materialDiv.height
                    : goodsDiv.height,
            }}
            modules={[Controller]}
            onSwiper={setSecondSwiper}
            controller={{ control: firstSwiper }}
            onSlideChange={onSlideChange}
          >
            <SwiperSlide style={{ height: resourceDiv.height }}>
              <TabsBody ref={resourceRef} kind="resource" />
            </SwiperSlide>
            <SwiperSlide style={{ height: materialDiv.height }}>
              <TabsBody ref={materialRef} kind="material" />
            </SwiperSlide>
            <SwiperSlide style={{ height: goodsDiv.height }}>
              <TabsBody ref={goodsRef} kind="goods" />
            </SwiperSlide>
          </Swiper>
        </div>
      </div>
    </main>
  );
};

export { Storage };
